import { useGlobalStore } from '@/stores';
import { ArrowForward, Business, LocalShipping } from '@mui/icons-material';
import { Box, ButtonBase, Chip, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ContactEmployee } from '@/api/contact-employee/contact-employee.types';
import { queryCache } from '@/config/query-client';

export function LinkButton({
  employee,
  index = 0,
}: {
  employee: ContactEmployee;
  index?: number;
}) {
  const queryClient = useQueryClient();
  const store = useGlobalStore();
  const navigate = useNavigate();

  const isLastUsed = store.contactEmployeeId === employee.id;
  const isCustomer = employee.businessPartner.type === 'cCustomer';
  const accentColor = isCustomer ? '#02344C' : '#e6a800';

  const handleClick = () => {
    queryClient.invalidateQueries({ queryKey: ['me'] });
    store.setContactEmployeeId(employee.id);
    store.setBusinessPartnerId(employee.businessPartner.id);
    if (isCustomer) {
      const bpId = employee.businessPartner.id;
      queryCache.prefetchBusinessPartnerItems(queryClient, bpId);
      queryCache.prefetchBusinessPartner(queryClient, bpId);
      queryCache.prefetchItemFacets(queryClient);
      queryCache.prefetchSalesPerson(queryClient, bpId);
      navigate('/bp/c');
    } else if (employee.businessPartner.type === 'cSupplier') {
      navigate('/bp/s');
    } else {
      console.error('lid no access');
    }
  };

  return (
    <ButtonBase
      onClick={handleClick}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 2,
        borderRadius: 3,
        bgcolor: 'white',
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        textAlign: 'left',
        transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        '&:hover': {
          borderColor: 'rgba(0,0,0,0.08)',
          boxShadow: '0 8px 28px rgba(2, 52, 76, 0.16)',
        },
        '&:hover .link-arrow': {
          transform: 'translateX(4px)',
          color: accentColor,
        },
        // Staggered entrance
        opacity: 0,
        animation: `fadeSlideIn 0.4s ease-out ${300 + index * 80}ms forwards`,
        '@keyframes fadeSlideIn': {
          from: { opacity: 0, transform: 'translateY(12px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2,
          bgcolor: accentColor,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {isCustomer ? (
          <Business sx={{ fontSize: 20 }} />
        ) : (
          <LocalShipping sx={{ fontSize: 20 }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: '0.9rem',
              lineHeight: 1.3,
              color: '#1a1a1a',
            }}
          >
            {employee.businessPartner.name}
          </Typography>
          {isLastUsed && (
            <Chip
              label="Zuletzt"
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 600,
                bgcolor: 'rgba(2, 52, 76, 0.08)',
                color: '#02344C',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: '0.75rem', color: '#777' }}
          >
            {employee.firstName} {employee.lastName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6rem',
              color: '#aaa',
              fontFamily: '"Roboto Mono", monospace',
              letterSpacing: '0.02em',
            }}
          >
            #{employee.businessPartner.id}
          </Typography>
        </Box>
      </Box>
      <ArrowForward
        className="link-arrow"
        sx={{
          color: '#bbb',
          fontSize: 18,
          flexShrink: 0,
          transition: 'transform 0.25s ease, color 0.25s ease',
        }}
      />
    </ButtonBase>
  );
}
